import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, paymentModeLabel } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import Receipt from "@/components/Receipt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Phone, MessageCircle, MapPin, Loader2, Plus, Trash2,
  Package, Truck, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const PAY_MODES = [
  { key: "cash", label: "Espèces" },
  { key: "bank", label: "Banque" },
  { key: "mobile_money", label: "Mobile Money" },
];

export default function CourierOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({ free_delivery_threshold: 50 });
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [confirm, setConfirm] = useState(false);

  // payment
  const [rhythm, setRhythm] = useState("");
  const [payMode, setPayMode] = useState("");
  const [amount, setAmount] = useState(0);
  const [reference, setReference] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/orders/${id}`), api.get("/services?active_only=true"), api.get("/settings"),
    ]).then(([o, s, st]) => {
      setOrder(o.data);
      setItems((o.data.items || []).map((it) => ({ ...it, _uid: crypto.randomUUID() })));
      setServices(s.data);
      setSettings(st.data);
    }).catch(() => toast.error("Commande introuvable")).finally(() => setLoading(false));
  }, [id]);

  const itemSub = (it) => it.billing_mode === "kilo" ? (Number(it.weight) || 0) * (Number(it.unit_price) || 0) : (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
  const serviceTotal = items.reduce((a, it) => a + itemSub(it), 0);
  const baseFee = order?.base_delivery_fee || 0;
  const freeThreshold = settings.free_delivery_threshold || 0;
  const effFee = order?.delivery_waived ? 0 : (freeThreshold && serviceTotal >= freeThreshold ? 0 : baseFee);
  const total = Math.max(serviceTotal + effFee - (Number(discount) || 0), 0);
  const alreadyPaid = order?.amount_paid || 0;

  const payNow = rhythm === "total" ? total : rhythm === "on_delivery" ? 0 : Number(amount) || 0;
  const balance = Math.max(total - payNow, 0);
  const remainingToCollect = Math.max(total - alreadyPaid, 0);
  const needPayMode = order?.status === "Nouveau" ? payNow > 0 : remainingToCollect > 0;

  const addItem = () => setItems((x) => [...x, { _uid: crypto.randomUUID(), service_id: "", name: "", billing_mode: "piece", unit_price: 0, quantity: 1, weight: 0 }]);
  const removeItem = (i) => setItems((x) => x.filter((_, idx) => idx !== i));
  const updItem = (i, patch) => setItems((x) => x.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const pickService = (i, sid) => {
    const svc = services.find((s) => s.id === sid);
    if (svc) updItem(i, { service_id: svc.id, name: svc.name, billing_mode: svc.billing_mode, unit_price: svc.unit_price });
  };

  const isCollect = order?.status === "Nouveau";

  const totalPieces = items.reduce((a, it) => a + (Number(it.quantity) || 0), 0);
  const kiloWeightsOk = items.filter((it) => it.billing_mode === "kilo").every((it) => (Number(it.weight) || 0) > 0);
  const hasService = items.length > 0 && items.every((it) => it.service_id);
  const missing = [];
  if (!hasService) missing.push("service");
  if (totalPieces <= 0) missing.push("pièces");
  if (!kiloWeightsOk) missing.push("poids (kg)");
  if (needPayMode && !payMode) missing.push("mode de paiement");
  if (!rhythm) missing.push("rythme");
  if (rhythm === "partial" && !((Number(amount) || 0) > 0)) missing.push("montant");
  const canCollect = isCollect && missing.length === 0;

  const doCollect = async () => {
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({ service_id: it.service_id || null, name: it.name, billing_mode: it.billing_mode, unit_price: Number(it.unit_price) || 0, quantity: Number(it.quantity) || 0, weight: Number(it.weight) || 0 })),
        discount: Number(discount) || 0,
        payment: { payment_rhythm: rhythm, amount_paid: payNow, payment_mode: payMode, reference },
      };
      const { data } = await api.post(`/courier/orders/${id}/collect`, payload);
      toast.success("Commande collectée");
      setOrder(data.order);
      setReceipt(data.receipt);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  const doDeliver = async () => {
    setBusy(true);
    try {
      const remaining = Math.max(total - alreadyPaid, 0);
      const { data } = await api.post(`/courier/orders/${id}/deliver`, {
        amount_paid: remaining, payment_mode: payMode, reference,
      });
      toast.success("Livraison confirmée");
      setOrder(data.order);
      setReceipt(data.receipt);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>;
  if (!order) return null;

  const done = order.status === "Livré" || (!isCollect && order.status === "Collecté");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((order.address || "") + " " + (order.commune_name || ""))}`;

  return (
    <div className="min-h-screen bg-slate-50 pb-40">
      <header className="sticky top-0 z-20 bg-white border-b px-4 h-14 flex items-center gap-3">
        <button data-testid="courier-back" onClick={() => navigate("/livreur")} className="p-1"><ArrowLeft className="w-6 h-6" /></button>
        <div className="flex-1">
          <div className="font-mono font-bold text-brand-700">{order.order_number}</div>
        </div>
        <StatusBadge status={order.status} />
      </header>

      <div className="p-4 space-y-4">
        {/* SECTION 1 - CLIENT */}
        <div className="bg-white border rounded-2xl p-4">
          <h3 className="font-semibold text-slate-800 mb-2">Client</h3>
          <div className="text-lg font-bold">{order.client_name}</div>
          <div className="text-sm text-slate-500 space-y-0.5 mt-1">
            <div>{order.address}</div>
            <div>{order.commune_name} {order.quartier ? `· ${order.quartier}` : ""}</div>
            <div>Réf : {order.reference || "—"}</div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <a data-testid="courier-call" href={`tel:${order.whatsapp}`} className="flex flex-col items-center gap-1 bg-slate-100 rounded-xl py-2.5 text-xs font-medium"><Phone className="w-5 h-5 text-brand-600" /> Appeler</a>
            <a data-testid="courier-whatsapp" href={`https://wa.me/${(order.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 bg-slate-100 rounded-xl py-2.5 text-xs font-medium"><MessageCircle className="w-5 h-5 text-emerald-600" /> WhatsApp</a>
            <a data-testid="courier-maps" href={mapsUrl} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 bg-slate-100 rounded-xl py-2.5 text-xs font-medium"><MapPin className="w-5 h-5 text-red-500" /> Localiser</a>
          </div>
        </div>

        {/* SECTION 2 - SERVICES */}
        <div className="bg-white border rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Détails de la commande</h3>
            {isCollect && <Button data-testid="courier-add-item" size="sm" variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Service</Button>}
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it._uid || i} className="bg-slate-50 rounded-xl p-3">
                {isCollect ? (
                  <>
                    <select
                      data-testid={`courier-item-service-${i}`}
                      value={it.service_id || ""}
                      onChange={(e) => pickService(i, e.target.value)}
                      className="w-full h-10 rounded-lg border px-2 text-sm bg-white"
                    >
                      <option value="">Choisir un service</option>
                      {services.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.billing_mode === "kilo" ? "kg" : "pièce"})</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-2 mt-2 items-end">
                      <div><Label className="text-xs">PU</Label><Input data-testid={`courier-item-pu-${i}`} className="h-10 mt-1 bg-slate-100 text-slate-500 cursor-not-allowed" type="number" value={it.unit_price} readOnly disabled /></div>
                      <div><Label className="text-xs">Poids (kg)</Label><Input data-testid={`courier-item-weight-${i}`} className={`h-10 mt-1 ${it.billing_mode === "kilo" && !((Number(it.weight) || 0) > 0) ? "border-red-400 ring-1 ring-red-300" : ""}`} type="number" inputMode="decimal" value={it.weight} onChange={(e) => updItem(i, { weight: e.target.value })} /></div>
                      <div><Label className="text-xs">Pièces</Label><Input data-testid={`courier-item-qty-${i}`} className="h-10 mt-1" type="number" inputMode="numeric" value={it.quantity} onChange={(e) => updItem(i, { quantity: e.target.value })} /></div>
                    </div>
                    <div className="text-right mt-1 text-sm"><span className="text-xs text-slate-400 mr-2">Facturé au {it.billing_mode === "kilo" ? "kg" : "pièce"} · Sous-total</span><b>{money(itemSub(it))}</b></div>
                    <button data-testid={`courier-item-remove-${i}`} onClick={() => removeItem(i)} className="text-red-500 text-xs mt-2 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span>{it.name} <span className="text-slate-400">{it.weight ? `${it.weight}kg` : ""}{it.weight && it.quantity ? " · " : ""}{it.quantity ? `x${it.quantity}` : ""}</span></span>
                    <b>{money(itemSub(it))}</b>
                  </div>
                )}
              </div>
            ))}
            {items.length === 0 && <p className="text-sm text-slate-400 text-center py-2">Aucun service</p>}
          </div>
          {isCollect && (
            <div className="mt-3 text-xs text-slate-400">La remise ne peut être appliquée que par l'administration.</div>
          )}
        </div>

        {/* SECTION 3 - FINANCIER */}
        <div className="bg-white border rounded-2xl p-4 text-sm space-y-1.5">
          <h3 className="font-semibold text-slate-800 mb-2">Récapitulatif financier</h3>
          <div className="flex justify-between"><span>Frais de service</span><b>{money(serviceTotal)}</b></div>
          <div className="flex justify-between"><span>Nombre de pièces</span><b className={totalPieces > 0 ? "" : "text-red-600"}>{totalPieces}</b></div>
          <div className="flex justify-between"><span>Frais de livraison</span><b>{money(effFee)}{effFee === 0 && baseFee > 0 && <span className="text-emerald-600 text-xs ml-1">gratuit</span>}</b></div>
          <div className="flex justify-between"><span>Remise</span><b>-{money(discount)}</b></div>
          <div className="flex justify-between text-lg border-t pt-1.5 mt-1"><span className="font-semibold">Total à payer</span><b className="text-brand-700">{money(total)}</b></div>
          {alreadyPaid > 0 && <div className="flex justify-between"><span>Déjà payé</span><b>{money(alreadyPaid)}</b></div>}
        </div>

        {/* SECTION 4 - PAIEMENT */}
        {!done && (
          <div className="bg-white border rounded-2xl p-4">
            <h3 className="font-semibold text-slate-800 mb-3">Paiement</h3>
            {isCollect ? (
              <>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[{ k: "total", l: "Total" }, { k: "partial", l: "Partiel" }, { k: "on_delivery", l: "À la livraison" }].map((r) => (
                    <button key={r.k} data-testid={`courier-rhythm-${r.k}`} onClick={() => setRhythm(r.k)} className={`py-2 rounded-xl text-sm font-medium border ${rhythm === r.k ? "bg-brand-600 text-white border-brand-600" : "bg-white border-slate-200"}`}>{r.l}</button>
                  ))}
                </div>
                {rhythm === "partial" && (
                  <div className="mb-3"><Label className="text-xs">Montant payé maintenant</Label><Input data-testid="courier-amount" className="h-11 mt-1 text-lg" type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 mb-3">Solde à encaisser : <b className="text-amber-600">{money(Math.max(total - alreadyPaid, 0))}</b>. Confirmez le paiement complet pour valider la livraison.</p>
            )}

            {needPayMode && (
              <>
                <Label className="text-xs">Mode de paiement</Label>
                <div className="grid grid-cols-3 gap-2 mt-1 mb-3">
                  {PAY_MODES.map((m) => (
                    <button key={m.key} data-testid={`courier-paymode-${m.key}`} onClick={() => setPayMode(m.key)} className={`py-2 rounded-xl text-sm font-medium border ${payMode === m.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>{m.label}</button>
                  ))}
                </div>
                <div><Label className="text-xs">Référence (optionnel)</Label><Input data-testid="courier-reference" className="h-10 mt-1" value={reference} onChange={(e) => setReference(e.target.value)} /></div>
              </>
            )}

            {isCollect && (
              <div className="bg-brand-50 rounded-xl p-3 mt-3 text-sm">
                <div className="flex justify-between"><span>Payé maintenant</span><b>{money(payNow)}</b></div>
                <div className="flex justify-between"><span>Solde restant</span><b className={balance > 0 ? "text-amber-600" : "text-emerald-600"}>{money(balance)}</b></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom action */}
      {!done && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t p-4">
          {isCollect ? (
            <>
              {!canCollect && (
                <p data-testid="courier-collect-missing" className="text-xs text-red-600 mb-2 text-center">Complétez : {missing.join(", ")}</p>
              )}
              <Button data-testid="courier-collect-btn" onClick={() => setConfirm(true)} disabled={busy || !canCollect} className="w-full h-14 text-lg bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
                <Package className="w-5 h-5 mr-2" /> COLLECTER
              </Button>
            </>
          ) : (
            <Button data-testid="courier-deliver-btn" onClick={() => setConfirm(true)} disabled={busy} className="w-full h-14 text-lg bg-emerald-600 hover:bg-emerald-700">
              <Truck className="w-5 h-5 mr-2" /> CONFIRMER LA LIVRAISON
            </Button>
          )}
        </div>
      )}

      {/* Confirm sheet */}
      {confirm && (
        <div className="fixed inset-0 z-40 bg-slate-900/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setConfirm(false)}>
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-5 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-6 h-6 text-brand-600" /><h3 className="text-lg font-bold font-heading">Vérification</h3></div>
            <div className="space-y-1 text-sm bg-slate-50 rounded-xl p-4">
              <div className="flex justify-between"><span>Client</span><b>{order.client_name}</b></div>
              <div className="flex justify-between"><span>Services</span><b>{items.length} article(s)</b></div>
              <div className="flex justify-between"><span>Pièces</span><b>{totalPieces}</b></div>
              <div className="flex justify-between"><span>Total</span><b>{money(total)}</b></div>
              {isCollect ? (
                <>
                  <div className="flex justify-between"><span>Paiement</span><b>{rhythm === "total" ? "Total" : rhythm === "partial" ? "Partiel" : "À la livraison"}</b></div>
                  <div className="flex justify-between"><span>Payé</span><b>{money(payNow)}</b></div>
                  <div className="flex justify-between"><span>Solde</span><b>{money(balance)}</b></div>
                </>
              ) : (
                <div className="flex justify-between"><span>Encaissement</span><b>{money(Math.max(total - alreadyPaid, 0))}{payMode ? ` (${paymentModeLabel[payMode]})` : ""}</b></div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" className="flex-1" onClick={() => setConfirm(false)}>Annuler</Button>
              <Button data-testid="courier-confirm-btn" className="flex-1 bg-brand-600 hover:bg-brand-700" disabled={busy} onClick={isCollect ? doCollect : doDeliver}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {receipt && <Receipt receipt={receipt} onClose={() => { setReceipt(null); navigate("/livreur"); }} />}
    </div>
  );
}
